import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { Star, Send, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { getReceiptByBookingNumber, createReview } from '../utils/api';

const ReviewPage = () => {
  const { bookingNumber } = useParams();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const captchaRef = useRef(null);

  useEffect(() => {
    fetchBooking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingNumber]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const data = await getReceiptByBookingNumber(bookingNumber);
      setBooking(data);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError('Booking not found or not eligible for review');
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaVerify = (token) => {
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const handleCaptchaExpire = () => {
    setCaptchaToken(null);
    setCaptchaError('Your security check expired. Please complete the verification again before submitting.');
    if (captchaRef.current) {
      try { captchaRef.current.resetCaptcha(); } catch (_) {}
    }
  };

  const handleCaptchaError = (err) => {
    setCaptchaError('The security check failed to load. Please try again.');
    setCaptchaToken(null);
    if (captchaRef.current) {
      try { captchaRef.current.resetCaptcha(); } catch (_) {}
    }
  };

  const describeReviewError = (err) => {
    const status = err?.response?.status;
    const data = err?.response?.data;

    if (status === 403) {
      return { message: 'Security verification failed. Please complete the hCaptcha again and resubmit.', resetCaptcha: true };
    }
    if (status === 400) {
      const detail = typeof data?.detail === 'string' ? data.detail : '';
      if (/captcha|verification/i.test(detail)) {
        return { message: detail || 'Please complete the security verification.', resetCaptcha: true };
      }
      return { message: detail || 'You have already reviewed this trip.' };
    }
    if (status === 422 && Array.isArray(data?.detail)) {
      const bullets = data.detail.map((e) => {
        const loc = Array.isArray(e.loc) ? e.loc : [];
        const leaf = String(loc[loc.length - 1] || '');
        return `• ${leaf || 'field'}: ${e.msg}`;
      });
      return { message: 'Please correct the highlighted issues:\n' + bullets.join('\n') };
    }
    if (status === 429) {
      const detail = typeof data?.detail === 'string' ? data.detail : 'Too many submissions. Please try again later.';
      return { message: detail };
    }
    if (err?.message && /network/i.test(err.message)) {
      return { message: 'Network error. Please check your connection and try again.' };
    }
    const detail = typeof data?.detail === 'string' ? data.detail : null;
    return { message: detail || 'Failed to submit review. Please try again.' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setCaptchaError('Please complete the security verification below before submitting.');
      setError('Security verification is required to submit your review.');
      return;
    }

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (comment.trim().length < 10) {
      setError('Please write at least 10 characters for your review');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      await createReview({
        bookingId: bookingNumber,
        rating,
        comment: comment.trim(),
        customerName: booking.passengerName,
        customerEmail: booking.passengerEmail,
        hCaptchaToken: captchaToken
      });

      setSubmitted(true);

      // Reset captcha
      setCaptchaToken(null);
      if (captchaRef.current) {
        try { captchaRef.current.resetCaptcha(); } catch (_) {}
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      const { message, resetCaptcha } = describeReviewError(err);
      setError(message);
      if (resetCaptcha) {
        setCaptchaToken(null);
        setCaptchaError('Please complete the security verification again.');
        if (captchaRef.current) {
          try { captchaRef.current.resetCaptcha(); } catch (_) {}
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-3xl shadow-xl text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-3xl shadow-xl border border-red-200">
            <div className="flex items-center space-x-3 text-red-600 mb-4">
              <AlertCircle className="w-6 h-6" />
              <h2 className="text-xl font-bold">Unable to Load Review</h2>
            </div>
            <p className="text-gray-700 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Go to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-12 rounded-3xl shadow-xl border border-green-200 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-400">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-xl text-gray-700 mb-2">
              Your review has been submitted successfully.
            </p>
            <p className="text-gray-600 mb-8">
              We appreciate your feedback and will review it shortly.
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-8 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors font-semibold"
            >
              Return to Homepage
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-cyan-100">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Rate Your Experience
            </h1>
            <p className="text-lg text-gray-600">
              How was your ride with Taxi-98?
            </p>
          </div>

          {/* Trip Summary */}
          <div className="bg-cyan-50 p-6 rounded-2xl mb-8 border border-cyan-200">
            <h3 className="font-semibold text-gray-900 mb-3">Trip Details</h3>
            <div className="space-y-2 text-sm">
              <p className="text-gray-700">
                <span className="font-medium">From:</span> {booking?.pickupLocation}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">To:</span> {booking?.dropoffLocation}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Date:</span> {booking?.date}
              </p>
              <p className="text-gray-700">
                <span className="font-medium">Booking:</span> #{bookingNumber}
              </p>
            </div>
          </div>

          {/* Review Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Star Rating */}
            <div>
              <label className="block text-gray-900 font-semibold mb-4 text-lg">
                Your Rating *
              </label>
              <div className="flex items-center justify-center space-x-3">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="focus:outline-none transform transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-12 h-12 transition-colors ${
                        star <= (hoverRating || rating)
                          ? 'text-yellow-500 fill-yellow-500'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-center mt-3 text-gray-600 font-medium">
                  {rating === 5 && 'Excellent!'}
                  {rating === 4 && 'Very Good!'}
                  {rating === 3 && 'Good'}
                  {rating === 2 && 'Fair'}
                  {rating === 1 && 'Poor'}
                </p>
              )}
            </div>

            {/* Comment */}
            <div>
              <label htmlFor="comment" className="block text-gray-900 font-semibold mb-3">
                Your Review *
              </label>
              <textarea
                id="comment"
                name="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
                rows="5"
                minLength="10"
                maxLength="1000"
                placeholder="Tell us about your experience with Taxi-98. What did you like? How was the driver? Was the vehicle clean?"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all duration-300"
              />
              <p className="text-sm text-gray-500 mt-2">
                {comment.length} / 1000 characters (minimum 10)
              </p>
            </div>

            {/* Security Verification */}
            <div>
              <label className="block text-gray-900 font-semibold mb-4 text-lg flex items-center">
                <Shield className="w-5 h-5 mr-2 text-cyan-600" />
                Security Verification *
              </label>
              <div className="flex flex-col items-center">
                <HCaptcha
                  ref={captchaRef}
                  sitekey={process.env.REACT_APP_HCAPTCHA_SITEKEY || '10000000-ffff-ffff-ffff-000000000001'}
                  onVerify={handleCaptchaVerify}
                  onExpire={handleCaptchaExpire}
                  onError={handleCaptchaError}
                  theme="light"
                  size="normal"
                />
                {captchaError && (
                  <p className="text-red-600 text-sm mt-2">{captchaError}</p>
                )}
                {captchaToken && (
                  <p className="text-green-600 text-sm mt-2 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" /> Verified
                  </p>
                )}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || rating === 0 || comment.trim().length < 10 || !captchaToken}
              className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-semibold text-lg hover:from-cyan-600 hover:to-blue-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Submit Review</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
